import { Check, ChevronDown, X } from 'lucide-react';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import { cn } from '@/shared/lib/utils';
import { ButtonVariant } from '@/shared/types/button.types';
import type { FamilyMember } from '@/shared/types/member.types';
import { Button } from './button';
import { MemberAvatar } from './member-avatar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { TruncatedText } from './truncated-text';

/**
 * Props for MemberSelector component
 */
export interface MemberSelectorProps {
  /** List of available family members to select from */
  readonly members: readonly FamilyMember[];
  /** Currently selected member IDs */
  readonly selected: readonly string[];
  /** Callback when selection changes */
  readonly onChange: (ids: string[]) => void;
  /** Allow selecting multiple members */
  readonly multiple?: boolean;
  /** Placeholder text when nothing is selected */
  readonly placeholder?: string;
  /** Additional CSS classes */
  readonly className?: string;
  /** Whether the selector is disabled */
  readonly disabled?: boolean;
}

/**
 * MemberSelector - Select one or multiple family members
 *
 * Provides a dropdown interface for selecting family members with avatars.
 * Supports both single and multiple selection modes.
 *
 * @example
 * ```tsx
 * // Single selection
 * <MemberSelector
 *   members={familyMembers}
 *   selected={[selectedId]}
 *   onChange={(ids) => setSelectedId(ids[0])}
 *   placeholder="Изберете член"
 * />
 *
 * // Multiple selection
 * <MemberSelector
 *   members={familyMembers}
 *   selected={selectedIds}
 *   onChange={setSelectedIds}
 *   multiple
 *   placeholder="Изберете участници"
 * />
 * ```
 */
export const MemberSelector = forwardRef<
  HTMLButtonElement,
  MemberSelectorProps
>(
  (
    {
      members,
      selected,
      onChange,
      multiple = false,
      placeholder = 'Изберете...',
      className,
      disabled = false,
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);

    // Get selected members for display
    const selectedMembers = useMemo(() => {
      return members.filter((m) => selected.includes(m.id));
    }, [members, selected]);

    // Handle member selection
    const handleSelect = useCallback(
      (memberId: string) => {
        if (multiple) {
          // Toggle selection in multiple mode
          const newSelected = selected.includes(memberId)
            ? selected.filter((id) => id !== memberId)
            : [...selected, memberId];
          onChange(newSelected);
        } else {
          // Replace selection in single mode
          onChange([memberId]);
          setOpen(false);
        }
      },
      [multiple, selected, onChange]
    );

    // Remove a selected member (multiple mode only)
    const handleRemove = useCallback(
      (memberId: string, event: React.MouseEvent) => {
        event.stopPropagation();
        onChange(selected.filter((id) => id !== memberId));
      },
      [selected, onChange]
    );

    // Clear all selections
    const handleClear = useCallback(
      (event: React.MouseEvent) => {
        event.stopPropagation();
        onChange([]);
      },
      [onChange]
    );

    // Get first selected member safely
    const firstSelectedMember = selectedMembers[0];

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant={ButtonVariant.Outline}
            role="combobox"
            aria-expanded={open}
            aria-label={placeholder}
            disabled={disabled}
            className={cn(
              'h-auto min-h-[40px] w-full justify-between',
              className
            )}
          >
            <div className="flex flex-1 flex-wrap items-center gap-1.5 text-left">
              {selectedMembers.length === 0 ? (
                <span className="text-muted-foreground">{placeholder}</span>
              ) : multiple ? (
                // Show chips for multiple selection
                selectedMembers.map((member) => (
                  <span
                    key={member.id}
                    className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-0.5 text-sm text-secondary-foreground"
                  >
                    <MemberAvatar member={member} size="sm" />
                    <TruncatedText maxWidth="max-w-[100px]">
                      {member.displayName}
                    </TruncatedText>
                    <button
                      type="button"
                      onClick={(e) => handleRemove(member.id, e)}
                      className="ml-0.5 rounded-full p-0.5 hover:bg-muted"
                      aria-label={`Премахни ${member.displayName}`}
                    >
                      <X className="size-3" aria-hidden="true" />
                    </button>
                  </span>
                ))
              ) : firstSelectedMember ? (
                // Show single selection
                <MemberAvatar member={firstSelectedMember} size="sm" showName />
              ) : null}
            </div>

            <div className="ml-2 flex shrink-0 items-center gap-1">
              {selectedMembers.length > 0 && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="rounded-full p-1 hover:bg-muted"
                  aria-label="Изчисти избора"
                >
                  <X
                    className="size-4 text-muted-foreground"
                    aria-hidden="true"
                  />
                </button>
              )}
              <ChevronDown
                className="size-4 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
            </div>
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-1"
          align="start"
        >
          <div
            className="max-h-[300px] overflow-y-auto"
            role="listbox"
            aria-multiselectable={multiple}
          >
            {members.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Няма налични членове
              </div>
            ) : (
              members.map((member) => {
                const isSelected = selected.includes(member.id);
                return (
                  <button
                    key={member.id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelect(member.id)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-md px-2 py-2 text-left',
                      'hover:bg-accent hover:text-accent-foreground',
                      'focus:bg-accent focus:text-accent-foreground focus:outline-none',
                      isSelected && 'bg-accent/50'
                    )}
                  >
                    {multiple && (
                      <div
                        className={cn(
                          'flex size-4 items-center justify-center rounded border',
                          isSelected
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-input'
                        )}
                      >
                        {isSelected && (
                          <Check className="size-3" aria-hidden="true" />
                        )}
                      </div>
                    )}
                    <MemberAvatar member={member} size="sm" showName />
                  </button>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);

MemberSelector.displayName = 'MemberSelector';
